<script setup>
import GroupedActionPanel from "@/components/actions/partials/GroupedActionPanel.vue";
import HidePageActions from "@/components/actions/buttons/HidePageActions.vue";
import CompletePageActions from "@/components/actions/buttons/CompletePageActions.vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import ActionTime from "@/components/actions/partials/ActionTime.vue";
import ActionRow from "@/components/actions/partials/ActionRow.vue";
import {getActions} from "@/composables/useActions";
import {computed, ref, watch} from "vue";
import _ from "lodash";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import {useStore} from "vuex";
import ZeroContent from "@/components/global-themed/ZeroContent.vue";
import ContentWizardButton from "@/components/actions/partials/settings/ContentWizardButton.vue";
import GroupedActionPanelTitle from "@/components/actions/partials/GroupedActionPanelTitle.vue";
import InspectPage from "@/components/actions/partials/InspectPage.vue";

const showAllPageOrCategory = ref();
const maxShowActions = 5;
const store = useStore();
const activeTab = computed(() => store.getters['actions/activeTab']);

const toggleShowAll = (group) => {
  if (group.groupType === 'page') {
    showAllPageOrCategory.value = showAllPageOrCategory.value === group.page.id ? undefined : group.page.id;
    return;
  }

  showAllPageOrCategory.value = showAllPageOrCategory.value === group.category ? undefined : group.category;
}

const {data: actions, isLoading} = getActions(activeTab);

const createPageActionGroup = (action) => {
  const page = action.page;
  const pageActions = _(actions.value).filter(action => action.page?.url === page.url).value() || [];
  const totalTime = pageActions.map(action => action.action_time).reduce((a, b) => a + b, 0);
  const showAll = showAllPageOrCategory.value === page.id;

  return {
    key: page.id,
    groupType: 'page',
    page,
    totalTime,
    actions: showAll ? pageActions : pageActions.slice(0, maxShowActions),
    totalActions: pageActions.length,
    moreActionsCount: Math.max(pageActions.length - maxShowActions, 0),
    showAll,
  };
}

const createCategoryActionGroup = (action) => {
  const category = action.category;
  const categoryActions = _(actions.value)
    .filter(action => action.category === category && !action.page)
    .value() || [];
    
  const totalTime = categoryActions.map(action => action.action_time).reduce((a, b) => a + b, 0);
  const showAll = showAllPageOrCategory.value === category;

  return {
    key: category,
    groupType: 'category',
    category,
    totalTime,
    actions: showAll ? categoryActions : categoryActions.slice(0, maxShowActions),
    totalActions: categoryActions.length,
    moreActionsCount: Math.max(categoryActions.length - maxShowActions, 0),
    showAll: showAll,
  };
}

const groupedActions = ref([]);

const updateGroupedActions = () => {
  if (!actions.value) return;

  // Clear actions from groups before updating, so that empty groups get removed
  groupedActions.value.forEach(group => {
    group.actions = [];
  });

  const groups = [...groupedActions.value];
  const updatedKeys = []; // Keep track of keys that have been updated to avoid overwriting multiple times
  const updateGroup = (key, group) => {
    const index = _(groups).findIndex({key: key});

    if (index !== -1) {
      groups[index] = group;
      return;
    }

    groups.push(group);
  }

  actions.value.forEach(action => {
    if (action.page && !updatedKeys.includes(action.page.id)) {
      updateGroup(action.page.id, createPageActionGroup(action));
      updatedKeys.push(action.page.id);
      return true;
    }

    if (updatedKeys.includes(action.category)) {
      return true;
    }

    updateGroup(action.category, createCategoryActionGroup(action));
    updatedKeys.push(action.category);
  });

  groupedActions.value = _(groups).filter(group => group?.actions.length > 0).value();
};

watch(showAllPageOrCategory, updateGroupedActions);
watch(actions, updateGroupedActions);
watch(activeTab, updateGroupedActions, { immediate: true });
</script>

<template>
  <div class="tw-flex tw-flex-col tw-gap-10 tw-h-[72vh] tw-overflow-y-auto visible-scrollbar">
    <LoadingBlock v-if="isLoading" />

    <ZeroContent v-else-if="!actions.length">
      Looks like there are no actions to display.
    </ZeroContent>

    <GroupedActionPanel
      v-for="(group, index) in groupedActions"
      v-else
      :key="index"
    >
      <template #title>
        <GroupedActionPanelTitle
          :title="group.groupType === 'page' ? group.page.url : group.category"
          :url="group.groupType === 'page'"
        />
      </template>

      <template #action-time>
        <ActionTime
          :minutes="group.totalTime"
          class="tw-font-semibold"
        />
      </template>
      <template #toolbar>
        <div
          v-if="group.groupType === 'page'"
          class="tw-flex tw-gap-2"
        >
          <InspectPage
            v-if="group.page.status < 600 && !group.page.added_via_sitemap"
            :page="group.page"
          />
          <CompletePageActions :page-id="group.page.id" />
          <HidePageActions :page-id="group.page.id" />
          <ContentWizardButton
            :page="group.page"
            :has-keywords-assigned="group.page.has_keyword_assigned"
            buttonClasses="tw-h-[42px]"
          />
        </div>
      </template>

      <template #actions>
        <ActionRow
          v-for="action in group.actions"
          :key="action.id"
          :action="action"
        />
      </template>

      <template #pagination>
        <div
          v-if="group.moreActionsCount > 0"
          class="tw-flex tw-justify-end tw-align-middle tw-gap-2 tw-p-4"
        >
          <div
            class="tw-flex-grow-0 tw-flex tw-align-middle tw-p-4 tw-gap-2"
          >
            <svg-vue
              class="tw-h-4 tw-w-4"
              icon="icons/bar-chart-2"
            />
            <span v-if="!group.showAll">
              {{
                `${group.moreActionsCount} more ${group.moreActionsCount === 1 ? 'action' : 'actions'} for this ${group.groupType}`
              }}
            </span>
            <span v-else>
              {{
                `Showing ${group.totalActions} ${group.totalActions === 1 ? 'action' : 'actions'} for this ${group.groupType}`
              }}
            </span>
          </div>
          <DefaultButton
            variant="primary-inverted"
            @click="toggleShowAll(group)"
          >
            {{ group.showAll ? 'View Less' : 'View All' }}
          </DefaultButton>
        </div>
      </template>
    </GroupedActionPanel>
  </div>
</template>
